import { nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

import { isApiError } from '@/shared/api/problem-details'
import { useAuthStore } from '@/stores/auth'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    anonymousOnly?: boolean
    requiresSystemAdmin?: boolean
  }
}

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', redirect: '/app/projects' },
    { path: '/setup', name: 'setup', component: () => import('@/views/SetupView.vue') },
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { anonymousOnly: true } },
    { path: '/register', name: 'register', component: () => import('@/views/RegisterView.vue'), meta: { anonymousOnly: true } },
    { path: '/forgot-password', name: 'forgot-password', component: () => import('@/views/ForgotPasswordView.vue'), meta: { anonymousOnly: true } },
    { path: '/reset-password/:ticketId', name: 'reset-password', component: () => import('@/views/ResetPasswordView.vue') },
    { path: '/s/:shareId', name: 'public-share', component: () => import('@/views/PublicShareView.vue') },
    { path: '/app/projects', name: 'projects', component: () => import('@/views/ProjectsView.vue'), meta: { requiresAuth: true } },
    { path: '/app/projects/shared', name: 'shared-projects', component: () => import('@/views/ProjectsView.vue'), meta: { requiresAuth: true } },
    { path: '/app/trash', name: 'trash', component: () => import('@/views/ProjectsView.vue'), meta: { requiresAuth: true } },
    { path: '/app/profile', name: 'profile', component: () => import('@/views/ProfileView.vue'), meta: { requiresAuth: true } },
    { path: '/app/templates', name: 'templates', component: () => import('@/views/TemplateManagementView.vue'), meta: { requiresAuth: true } },
    { path: '/app/admin', name: 'system-admin', component: () => import('@/views/SystemAdminView.vue'), meta: { requiresAuth: true, requiresSystemAdmin: true } },
    { path: '/app/projects/:id/edit', name: 'project-edit', component: () => import('@/views/ProjectWorkspaceView.vue'), meta: { requiresAuth: true } },
    { path: '/app/projects/:id/view', name: 'project-view', component: () => import('@/views/ProjectWorkspaceView.vue'), meta: { requiresAuth: true } },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue') },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.path === from.path) return false
    return { top: 0 }
  },
})

router.beforeEach(async (to) => {
  const authStore = useAuthStore()
  if (to.meta.requiresAuth || to.meta.anonymousOnly) {
    try {
      await authStore.hydrate()
    } catch (error) {
      if (isApiError(error) && error.code === 'SETUP_REQUIRED') {
        return { name: 'setup' }
      }
      if (to.meta.requiresAuth) {
        return { name: 'login', query: { redirect: to.fullPath, serviceUnavailable: '1' } }
      }
      return true
    }
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.meta.requiresSystemAdmin && !authStore.isSystemAdmin) {
    return { name: 'projects' }
  }
  if (to.meta.anonymousOnly && authStore.isAuthenticated) {
    return { name: 'projects' }
  }
  return true
})

router.afterEach(async () => {
  await nextTick()
  window.requestAnimationFrame(() => {
    document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true })
  })
})
